import React, { useEffect, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceLine } from "recharts";
import { LineChart as LineIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Panel } from "./ui";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";

const PERIODS = ["1y", "2y", "5y"];

/**
 * An investor's gain against an index, in percent.
 *
 * Both lines are **cumulative percentage gain**, not indexed portfolio value. That
 * distinction is the whole point: portfolio value rises when someone adds money, so
 * the old chart showed a deposit as outperformance. The portfolio line here is the
 * time-weighted, flow-adjusted return the leaderboard already ranks on, so this chart
 * and the badge on the investor's card can never tell different stories.
 *
 * Safe to show at any tier — two percentage curves say nothing about what is held or
 * how much money is involved, and it is the strongest argument the investor has.
 */
export default function InvestorBenchmark({ investorId }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("1y");
  const [symbol, setSymbol] = useState("sp500");
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.investorBenchmark(investorId, period, symbol)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [investorId, period, symbol]);

  const beat = data?.portfolio_pct != null && data?.benchmark_pct != null
    ? data.portfolio_pct - data.benchmark_pct : null;

  return (
    <Panel className="card p-5 sm:p-6" testid="investor-benchmark">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <LineIcon size={17} className="text-brand-500" />
          <h3 className="font-heading font-semibold tracking-tight text-lg">
            {t("investorBenchmark.title", { benchmark: data?.benchmark_symbol || "S&P 500" })}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
            {PERIODS.map((p) => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`ib-period-${p}`}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md ${period === p ? "bg-white text-brand-600 shadow-sm" : "text-slate-500"}`}>
                {p}
              </button>
            ))}
          </div>
          <select data-testid="ib-symbol" value={symbol} onChange={(e) => setSymbol(e.target.value)}
                  className="input py-1.5 w-auto text-sm font-medium">
            {(data?.options || [{ value: "sp500", label: "S&P 500" }]).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {!data || !data.series?.length ? (
        <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">
          {t("analytics.notEnoughHistory")}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4">
            <div>
              <div className="label">{t("investorBenchmark.investor")}</div>
              <div className={`text-2xl font-heading font-semibold tabular mt-0.5 ${(data.portfolio_pct ?? 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                   data-testid="ib-portfolio-pct">
                {data.portfolio_pct == null ? "—" : `${data.portfolio_pct > 0 ? "+" : ""}${data.portfolio_pct}%`}
              </div>
            </div>
            <div>
              <div className="label">{data.benchmark_symbol}</div>
              <div className="text-2xl font-heading font-semibold tabular mt-0.5 text-slate-500"
                   data-testid="ib-benchmark-pct">
                {data.benchmark_pct == null ? "—" : `${data.benchmark_pct > 0 ? "+" : ""}${data.benchmark_pct}%`}
              </div>
            </div>
            {beat != null && (
              <div>
                <div className="label">{t("investorBenchmark.difference")}</div>
                <div className={`text-2xl font-heading font-semibold tabular mt-0.5 ${beat >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                     data-testid="ib-difference">
                  {beat > 0 ? "+" : ""}{Math.round(beat * 100) / 100} pp
                </div>
              </div>
            )}
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series} margin={{ top: 6, right: 6, left: -10, bottom: 0 }}>
              <XAxis dataKey="date" tickFormatter={(v) => shortDate(v)} tick={{ fontSize: 11, fill: "#94a3b8" }}
                     minTickGap={50} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                     width={48} tickFormatter={(v) => `${v}%`} />
              {/* Zero is the only line that matters on a gain chart. */}
              <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="3 3" />
              <Tooltip formatter={(v, n) => [v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`, n]}
                       labelFormatter={(l) => shortDate(l)}
                       contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="portfolio" name={t("investorBenchmark.investor")}
                    stroke="#0052FF" strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="benchmark" name={data.benchmark_symbol}
                    stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-slate-400 mt-2">{t("investorBenchmark.note")}</p>
        </>
      )}
    </Panel>
  );
}
